import { intersection, isEqual, isNil, uniq } from 'lodash-es'
import { createSelector, Selector } from '@reduxjs/toolkit'
import { GlobalState } from 'store'
import { canTradeSector, hasTradingRelationship, TradingEntityHelper, OrganisationHelper } from 'model'
import {
  TradingEntity,
  EntityType,
  TradingEntityDetail,
  OrganisationType,
  TradingEntityWithAlias,
  TradingEntityUpdate,
  EntityStatus,
  Side,
  Organisation,
  Instrument,
  OrderDetail,
  SettlementVenue,
  CounterpartyNameOrAliasDetail,
  MarketType
} from 'api'
import {
  createAllEntitiesSelector,
  createEntitySelector,
  createAllEntityUpdatesSelector,
  createEntityGetterSelector,
  createAllEntitiesSortedSelector,
  createEntityUpdateGetterSelector
} from '../entitySelectors'
import { createAuthorisedUserSelector } from '../connectionSelectors'
import { createCollateralsFromInstrumentIdGetterSelector, createInstrumentProductSectorIdSelector } from '../instrumentSelectors'
import { isOwnTradingEntityGetterSelector } from '../adminSelectors'
import { tradingRelationshipsGetterSelector } from '../tradingRelationshipSelectors'
import { tradingEntityMetasGetterSelector } from './tradingEntityMetaSelectors'
import { compareInsensitive, Nullable } from 'utils'
import { isNotNil } from 'utils/predicate'
import {
  createFilteredTradingEntitiesSelector,
  createTradingEntityCanTradeCollaterals,
  createTradingEntityCanTradeCollateralBasketsSelector
} from './tradingEntityFilterSelectors'
import { createTradingEntityFiltersForInstrumentSelector } from './tradingEntityFiltersForInstrumentSelector'
import { createTradingEntityFiltersForStagingMyEntitySelector } from './tradingEntityFiltersForStagingMyEntitySelector'
import {
  CounterpartyFilter,
  createTradingEntityFiltersForCounterpartyListSelector
} from './tradingEntityFiltersForCounterpartyListSelector'

const NULL_SELECTOR = () => null
const IS_EQUAL_RESULT_SELECTOR_OPTIONS = {
  memoizeOptions: {
    resultEqualityCheck: isEqual
  }
}

export const tradingEntityGetterSelector = createEntityGetterSelector(EntityType.TRADING_ENTITY)

export const tradingEntityCompare = (te1: TradingEntity, te2: TradingEntity) =>
  compareInsensitive(TradingEntityHelper.getName(te1), TradingEntityHelper.getName(te2))

const tradingEntitiesSortAndMapToId = (tes: TradingEntity[]): string[] => tes.sort(tradingEntityCompare).map(te => te.id)

// Trading Entity Selectors
export const allTradingEntitiesSelector: Selector<GlobalState, TradingEntity[]> = createSelector(
  createAllEntitiesSelector(EntityType.TRADING_ENTITY),
  tradingEntities => tradingEntities
)

export const allActiveTradingEntitiesSelector: Selector<GlobalState, TradingEntity[]> = createSelector(
  allTradingEntitiesSelector,
  tradingEntities => tradingEntities.filter(tradingEntity => tradingEntity.status === EntityStatus.ENABLED)
)

export const tradingEntitiesGetterSelector: Selector<
  GlobalState,
  (tradingEntityIds: string[]) => TradingEntity[] | null
> = createSelector(allTradingEntitiesSelector, allTradingEntities => (tradingEntityIds: string[]) =>
  allTradingEntities.filter(tradingEntity => tradingEntityIds.includes(tradingEntity.id))
)

export const allBuySideTradingEntityIdsSelector: Selector<GlobalState, string[]> = createSelector(
  createAllEntitiesSortedSelector(EntityType.TRADING_ENTITY),
  createAllEntitiesSortedSelector(EntityType.ORGANISATION),
  (tradingEntities, organisations) => {
    return tradingEntities
      .filter(tradingEntity =>
        organisations.some(
          organisation =>
            organisation.id === tradingEntity.organisationId && organisation.organisationTypes.includes(OrganisationType.BUY_SIDE)
        )
      )
      .map(tradingEntity => tradingEntity.id)
  }
)

export const allSellSideTradingEntityIdsSelector: Selector<GlobalState, string[]> = createSelector(
  createAllEntitiesSortedSelector(EntityType.TRADING_ENTITY),
  createAllEntitiesSortedSelector(EntityType.ORGANISATION),
  (tradingEntities, organisations) => {
    return tradingEntities
      .filter(tradingEntity =>
        organisations.some(
          organisation =>
            organisation.id === tradingEntity.organisationId && organisation.organisationTypes.includes(OrganisationType.SELL_SIDE)
        )
      )
      .map(tradingEntity => tradingEntity.id)
  }
)

export function createTradingEntitySelector<TProps>(
  tradingEntityIdSelector: Selector<{}, string | null | undefined, [TProps]>
): Selector<GlobalState, TradingEntity | null | undefined, [TProps]> {
  return createEntitySelector(tradingEntityIdSelector, EntityType.TRADING_ENTITY)
}

export const createTradingEntityByIdGetterSelector: Selector<
  GlobalState,
  (tradingEntityId: string) => TradingEntity | null
> = createSelector(createAllEntitiesSelector(EntityType.TRADING_ENTITY), tradingEntities => (tradingEntityId: string) =>
  tradingEntities.find(tradingEntity => tradingEntity.id === tradingEntityId) || null
)

export function createTradingEntityListForStagingMyEntitySelector<TProps>(orderDetailSelector: Selector<{}, OrderDetail, [TProps]>) {
  const filtersSelector = createTradingEntityFiltersForStagingMyEntitySelector(orderDetailSelector)
  return createSelector(
    [createFilteredTradingEntitiesSelector(filtersSelector)],
    tradingEntities => tradingEntitiesSortAndMapToId(tradingEntities),
    IS_EQUAL_RESULT_SELECTOR_OPTIONS
  )
}

export function createFundListByInstrumentSelector<TProps>(
  instrumentIdSelector: Selector<{}, string | null | undefined, [TProps]> = NULL_SELECTOR
) {
  const filtersSelector = createTradingEntityFiltersForInstrumentSelector(instrumentIdSelector)
  return createSelector(
    [createFilteredTradingEntitiesSelector(filtersSelector)],
    tradingEntities => tradingEntitiesSortAndMapToId(tradingEntities),
    IS_EQUAL_RESULT_SELECTOR_OPTIONS
  )
}

export function createPermissionedTradingEntitiesByInstrumentsAndTradingEntityRelationshipsGetterSelector() {
  const tradingRelationshipsSelector = createAllEntitiesSelector(EntityType.TRADING_RELATIONSHIP)
  return (state: GlobalState) => (instrumentIds: string[], counterpartyTradingEntityIds: string[]) => {
    const tradingRelationships = tradingRelationshipsSelector(state)

    const permissionedTradingEntitiesAcrossInstruments = intersection(
      ...instrumentIds.map(id => {
        const filtersSelector = createTradingEntityFiltersForInstrumentSelector(() => id)
        return createFilteredTradingEntitiesSelector(filtersSelector)(state, undefined)
      })
    )
    return tradingEntitiesSortAndMapToId(
      intersection(
        ...counterpartyTradingEntityIds.map(cptyId =>
          permissionedTradingEntitiesAcrossInstruments.filter(tradingEntity =>
            hasTradingRelationship(tradingEntity.id, cptyId, tradingRelationships, false, [])
          )
        )
      )
    )
  }
}

export const createOrganisationsTradingEntityRelationshipsGetterSelector = () =>
  createSelector(
    [
      createAllEntitiesSelector(EntityType.TRADING_RELATIONSHIP),
      createEntityGetterSelector(EntityType.ORGANISATION),
      createEntityGetterSelector(EntityType.TRADING_ENTITY),
      createCollateralsFromInstrumentIdGetterSelector(),
      createTradingEntityCanTradeCollateralBasketsSelector
    ],
    (relationships, getOrganisation, getTradingEntity, getCollateralsFromInstrumentId, createTradingEntityCanTradeCollateralBaskets) => (
      tradingEntityId: string,
      side: Side,
      instrument: Instrument
    ) => {
      const { collateral, collateralBasket } = getCollateralsFromInstrumentId(instrument.id)
      const tradingEntityCanTradeCollateralBaskets = createTradingEntityCanTradeCollateralBaskets(
        collateralBasket ? [{ collateralBasket, marketType: null }] : []
      )
      const tradingEntityCanTradeCollateral = createTradingEntityCanTradeCollaterals(collateral ? [{ collateral, marketType: null }] : [])
      return uniq(
        relationships
          .filter(relationship =>
            hasTradingRelationship(
              side === Side.BUY ? tradingEntityId : relationship.buyTradingEntity,
              side === Side.SELL ? tradingEntityId : relationship.sellTradingEntity,
              relationships,
              false,
              []
            )
          )
          .reduce<Organisation[]>((organisations, relationship) => {
            const tradingEntity = getTradingEntity(side === Side.SELL ? relationship.buyTradingEntity : relationship.sellTradingEntity)
            if (
              isNotNil(tradingEntity) &&
              tradingEntityCanTradeCollateralBaskets(tradingEntity) &&
              tradingEntityCanTradeCollateral(tradingEntity)
            ) {
              const organisationId = tradingEntity?.organisationId
              const organisation = isNotNil(organisationId) ? getOrganisation(organisationId) : undefined
              return isNotNil(organisation) ? [...organisations, organisation] : organisations
            }
            return organisations
          }, [])
      ).sort((o1, o2) => compareInsensitive(OrganisationHelper.getName(o1), OrganisationHelper.getName(o2)))
    }
  )

export const createCounterpartyListSelector = <TProps>(
  filtersSelector: Selector<{}, CounterpartyFilter[], [TProps]>,
  counterpartySettlementVenueSelector: Selector<{}, Nullable<SettlementVenue>, [TProps]>,
  clearingHouseIdSelector: Selector<{}, string | null, [TProps]>,
  organisationIdSelector: Selector<{}, Nullable<string>, [TProps]>
): Selector<GlobalState, string[], [TProps]> =>
  createSelector(
    [
      createFilteredTradingEntitiesSelector(
        createTradingEntityFiltersForCounterpartyListSelector(
          filtersSelector,
          counterpartySettlementVenueSelector,
          clearingHouseIdSelector,
          organisationIdSelector
        )
      )
    ],
    tradingEntities => {
      return uniq(tradingEntitiesSortAndMapToId(tradingEntities))
    }
  )

export const createCounterpartyNamesAndAliasesListDetailsSelector = <TProps>(
  filtersSelector: Selector<{}, CounterpartyFilter[], [TProps]>,
  counterpartySettlementVenueSelector: Selector<{}, Nullable<SettlementVenue>, [TProps]>,
  clearingHouseIdSelector: Selector<{}, string | null, [TProps]>,
  organisationIdSelector: Selector<{}, Nullable<string>, [TProps]>,
  showAliasSelector: Selector<{}, boolean | undefined, [TProps]>
): Selector<GlobalState, CounterpartyNameOrAliasDetail[], [TProps]> =>
  createSelector(
    [
      createFilteredTradingEntitiesSelector(
        createTradingEntityFiltersForCounterpartyListSelector(
          filtersSelector,
          counterpartySettlementVenueSelector,
          clearingHouseIdSelector,
          organisationIdSelector
        )
      ),
      createAllEntitiesSelector(EntityType.TRADING_ENTITY_META),
      filtersSelector,
      showAliasSelector
    ],
    (tradingEntities, allMeta, filters, showAlias) => {
      const fundIds = filters.map(f => f.fundId)
      const shouldShowAliases = showAlias && fundIds.length === 1

      const counterpartyNamesAndAliasesDetails: CounterpartyNameOrAliasDetail[] = tradingEntities
        .sort(tradingEntityCompare)
        .flatMap(subjectTradingEntity => {
          const meta = allMeta.find(
            m => fundIds.some(id => id === m.ownerTradingEntityId) && m.subjectTradingEntityId === subjectTradingEntity.id
          )

          if (meta?.aliasTypeAndValues && meta?.aliasTypeAndValues?.length >= 1 && shouldShowAliases) {
            return meta?.aliasTypeAndValues
              .filter(a => a.value && a.type)
              .map(alias => ({
                tradingEntityMeta: meta,
                publishedName: subjectTradingEntity.publishedName,
                counterpartyId: subjectTradingEntity.id,
                alias: alias.value!
              }))
          }

          return [
            {
              publishedName: subjectTradingEntity.publishedName,
              counterpartyId: subjectTradingEntity.id
            }
          ]
        })

      return counterpartyNamesAndAliasesDetails
    }
  )

export const hasUsSponsoredRelationshipSelector = createSelector(
  [createAllEntitiesSelector(EntityType.TRADING_RELATIONSHIP)],
  tradingRelationships => (buySideId: string, sellSideId: string) =>
    hasTradingRelationship(buySideId, sellSideId, tradingRelationships, true, [])
)

export function createOrganisationTradingEntityListSelector<TProps>(
  organisationIdSelector: Selector<{}, string | null, [TProps]>
): Selector<GlobalState, string[], [TProps]> {
  return createSelector(
    [createAllEntitiesSelector(EntityType.TRADING_ENTITY), organisationIdSelector],
    (allTradingEntities, organisationId) => {
      return tradingEntitiesSortAndMapToId(allTradingEntities.filter(tradingEntity => tradingEntity.organisationId === organisationId))
    }
  )
}

export function createOrganisationTypeTradingEntityListSelector<TProps>(
  organisationIdSelector: Selector<{}, string | null, [TProps]>,
  organisationTypeSelector: Selector<{}, OrganisationType, [TProps]>
): Selector<GlobalState, string[], [TProps]> {
  return createSelector(
    createAllEntitiesSelector(EntityType.TRADING_ENTITY),
    createEntityGetterSelector(EntityType.ORGANISATION),
    organisationIdSelector,
    organisationTypeSelector,
    (allTradingEntities, getOrganisation, organisationId, organisationType) => {
      return tradingEntitiesSortAndMapToId(
        allTradingEntities
          .filter(tradingEntity => tradingEntity.organisationId !== organisationId)
          .filter(tradingEntity => {
            const organisation = getOrganisation(tradingEntity.organisationId)
            return organisation && organisation.organisationTypes.includes(organisationType)
          })
      )
    }
  )
}

export function createDealTradingEntityListSelector<TProps>(
  organisationIdSelector: Selector<{}, string | null, [TProps]>,
  isinSelector: Selector<{}, string | null, [TProps]>,
  tradingEntityIdSelector: Selector<{}, string | null, [TProps]>,
  usSponsoredClearingSelector: Selector<{}, boolean, [TProps]>,
  marketTypeSelector: Selector<{}, MarketType | null, [TProps]>,
  shouldReturnFunds: boolean
): Selector<GlobalState, string[], [TProps]> {
  return createSelector(
    createAllEntitiesSelector(EntityType.TRADING_ENTITY),
    createAllEntitiesSelector(EntityType.TRADING_RELATIONSHIP),
    createInstrumentProductSectorIdSelector(isinSelector),
    tradingEntityIdSelector,
    organisationIdSelector,
    usSponsoredClearingSelector,
    marketTypeSelector,
    (allTradingEntities, tradingRelationships, productSectorId, tradingEntityId, organisationId, usSponsoredClearing, marketType) => {
      return tradingEntitiesSortAndMapToId(
        allTradingEntities.filter(tradingEntity => {
          const fundId = shouldReturnFunds ? tradingEntity.id : tradingEntityId
          const counterpartyId = shouldReturnFunds ? tradingEntityId : tradingEntity.id
          if (tradingEntity.organisationId !== organisationId) {
            return false
          }

          if (isNil(tradingEntityId) || !hasTradingRelationship(fundId!, counterpartyId!, tradingRelationships, usSponsoredClearing, [])) {
            // TODO Add MLAs from the deal
            return false
          }

          if (isNil(productSectorId) || !canTradeSector(productSectorId, tradingEntity.productRights, marketType)) {
            return false
          }

          return true
        })
      )
    }
  )
}

export function createUserTradingEntityListSelector<TProps>(
  userIdSelector: Selector<{}, string | null, [TProps]>
): Selector<GlobalState, string[], [TProps]> {
  return createSelector(
    [
      createAllEntitiesSelector(EntityType.TRADING_ENTITY),
      createAllEntitiesSelector(EntityType.TRADING_RELATIONSHIP),
      createEntityGetterSelector(EntityType.USER),
      userIdSelector
    ],
    (allTradingEntities, tradingRelationships, getUser, userId) => {
      const user = userId ? getUser(userId) : null
      if (user === null || user.tradingEntityDetails.length === 0) {
        return []
      } else {
        return tradingEntitiesSortAndMapToId(
          allTradingEntities.filter(tradingEntity => {
            if (
              user.tradingEntityDetails.some(
                ({ tradingEntityId: userTradingEntityId }) =>
                  // TODO Check if MLA needs to be passed in here
                  hasTradingRelationship(tradingEntity.id, userTradingEntityId, tradingRelationships, false, []) ||
                  hasTradingRelationship(userTradingEntityId, tradingEntity.id, tradingRelationships, false, [])
              )
            ) {
              return true
            } else {
              return false
            }
          })
        )
      }
    }
  )
}

export const tradingEntityByShortNameAndOrgShortCodeGetterSelector: Selector<
  GlobalState,
  (orgShortCode: string, shortName: string) => TradingEntity | null
> = createSelector(
  [createAllEntitiesSelector(EntityType.TRADING_ENTITY), createEntityGetterSelector(EntityType.ORGANISATION)],
  (allTradingEntities, getOrganisation) => (orgShortCode, shortName) =>
    allTradingEntities.find(tradingEntity => {
      const organisation = getOrganisation(tradingEntity.organisationId)
      return organisation?.shortCode === orgShortCode && tradingEntity.shortName === shortName
    }) ?? null
)

export const tradingEntityByShortNameAndOrgIdGetterSelector: Selector<
  GlobalState,
  (organisationId: string, tradingEntityShortName: string) => TradingEntity | null
> = createSelector([createAllEntitiesSelector(EntityType.TRADING_ENTITY)], allTradingEntities => (organisationId, tradingEntityShortName) =>
  allTradingEntities.find(tradingEntity => {
    return tradingEntity.organisationId === organisationId && tradingEntity.shortName === tradingEntityShortName
  }) ?? null
)

export const createTradingEntityByShortNameGetterSelector: Selector<
  GlobalState,
  (shortName: string) => TradingEntity | null
> = createSelector([createAllEntitiesSelector(EntityType.TRADING_ENTITY)], allTradingEntities => shortName =>
  allTradingEntities.find(tradingEntity => tradingEntity.shortName.toLowerCase() === shortName.toLowerCase()) ?? null
)

export const createActiveTradingEntitiesByOrganisationIdGetterSelector = createSelector(
  createAllEntitiesSelector(EntityType.TRADING_ENTITY),
  tradingEntities => (organisationId: string) =>
    tradingEntities
      .filter(tradingEntity => tradingEntity.organisationId === organisationId)
      .filter(tradingEntity => tradingEntity.status === EntityStatus.ENABLED)
)

export const createActiveTradingEntityIdsByOrganisationIdGetterSelector = createSelector(
  [createAllEntitiesSelector(EntityType.TRADING_ENTITY)],
  tradingEntities => (organisationId: string) =>
    tradingEntities
      .filter(tradingEntity => tradingEntity.organisationId === organisationId)
      .filter(tradingEntity => tradingEntity.status === EntityStatus.ENABLED)
      .map(tradingEntity => tradingEntity.id)
)

export function createTradingEntityWithAliasGetterSelector(): Selector<
  GlobalState,
  (ownerTradingEntityId: string, subjectTradingEntityId: string) => TradingEntityWithAlias | null
> {
  // this selector is kept for backwards compatibility, previously we had only one alias in each TradingEntityMeta, not multiple
  // there are some places that still use this selector. we only add alias if there's one alias
  return createSelector(
    createEntityGetterSelector(EntityType.TRADING_ENTITY),
    createAllEntitiesSelector(EntityType.TRADING_ENTITY_META),
    (getTradingEntity, allTradingEntityMeta) => (ownerTradingEntityId: string, subjectTradingEntityId: string) => {
      const subjectTradingEntity = getTradingEntity(subjectTradingEntityId)
      if (subjectTradingEntity) {
        const tradingEntityMetaWithAlias = allTradingEntityMeta.find(
          meta => meta.ownerTradingEntityId === ownerTradingEntityId && meta.subjectTradingEntityId === subjectTradingEntityId
        )

        const alias =
          tradingEntityMetaWithAlias?.aliasTypeAndValues.length === 1 ? tradingEntityMetaWithAlias?.aliasTypeAndValues[0]?.value : undefined

        return tradingEntityMetaWithAlias ? { ...subjectTradingEntity, alias } : subjectTradingEntity
      } else {
        return null
      }
    }
  )
}

export function createTradingEntityWithOptionalAliasGetterSelector<TProps>(
  ownerTradingEntityIdSelector: Selector<{}, string | null, [TProps]>,
  showAliasSelector: Selector<{}, boolean | undefined, [TProps]>
): Selector<GlobalState, (subjectTradingEntityId: string) => TradingEntityWithAlias | null, [TProps]> {
  return createSelector(
    [
      createEntityGetterSelector(EntityType.TRADING_ENTITY),
      createTradingEntityWithAliasGetterSelector(),
      ownerTradingEntityIdSelector,
      showAliasSelector
    ],
    (getTradingEntity, getTradingEntityWithAlias, ownerTradingEntityId, showAlias) => (subjectTradingEntityId: string) => {
      if (!ownerTradingEntityId) {
        return getTradingEntity(subjectTradingEntityId)
      } else {
        return showAlias
          ? getTradingEntityWithAlias(ownerTradingEntityId, subjectTradingEntityId)
          : getTradingEntity(subjectTradingEntityId)
      }
    }
  )
}

//Trading Entity Details Selectors
export const tradingEntityDetailGetterSelector: Selector<
  GlobalState,
  (update: TradingEntityUpdate) => TradingEntityDetail
> = createSelector(
  createEntityGetterSelector(EntityType.ORGANISATION),
  tradingRelationshipsGetterSelector,
  tradingEntityMetasGetterSelector,
  (getOrganisation, getTradingRelationships, getTradingEntityMeta) => {
    return (update: TradingEntityUpdate) => ({
      tradingEntity: update.entity,
      organisation: getOrganisation(update.entity.organisationId),
      tradingRelationships: getTradingRelationships(update.entityId),
      tradingEntityMeta: getTradingEntityMeta(update.entityId),
      availableEvents: update.availableEvents
    })
  }
)

export const tradingEntityDetailsByIdsSortedSelector: Selector<GlobalState, TradingEntityDetail[], [string[]]> = createSelector(
  [
    createEntityUpdateGetterSelector(EntityType.TRADING_ENTITY),
    tradingEntityDetailGetterSelector,
    (_, tradingEntityIds: string[]) => tradingEntityIds
  ],
  (getTradingEntityUpdate, getTradingEntityDetails, tradingEntityIds) => {
    return tradingEntityIds.reduce((acc, tradingEntityId) => {
      const update = getTradingEntityUpdate(tradingEntityId)
      if (update) {
        acc.push(getTradingEntityDetails(update))
      }
      return acc.sort((te1, te2) =>
        compareInsensitive(TradingEntityHelper.getName(te1.tradingEntity), TradingEntityHelper.getName(te2.tradingEntity))
      )
    }, [] as TradingEntityDetail[])
  }
)

export const allTradingEntityDetailsSelector: Selector<GlobalState, TradingEntityDetail[]> = createSelector(
  createAllEntityUpdatesSelector(EntityType.TRADING_ENTITY),
  isOwnTradingEntityGetterSelector,
  tradingEntityDetailGetterSelector,
  (tradingEntityUpdates, getIsOwnTradingEntity, getTradingEntityDetails) => {
    return tradingEntityUpdates.filter(update => getIsOwnTradingEntity(update.entityId)).map(update => getTradingEntityDetails(update))
  }
)

export const allActiveTradingEntityDetailsSelector: Selector<GlobalState, TradingEntityDetail[]> = createSelector(
  allTradingEntityDetailsSelector,
  allTradingEntityDetails => {
    return allTradingEntityDetails.filter(
      (tradingEntityDetail: TradingEntityDetail) => tradingEntityDetail.tradingEntity.status === EntityStatus.ENABLED
    )
  }
)

export const allActiveTradingEntityDetailsSortedByOrganisationSelector: Selector<GlobalState, TradingEntityDetail[]> = createSelector(
  allActiveTradingEntityDetailsSelector,
  allActiveTradingEntityDetails => {
    return allActiveTradingEntityDetails.sort((te1, te2) => {
      const organisation1 = te1.organisation
      const organisation2 = te2.organisation
      return isNotNil(organisation1) && isNotNil(organisation2)
        ? compareInsensitive(OrganisationHelper.getName(organisation1), OrganisationHelper.getName(organisation2))
        : 0
    })
  }
)

export const userEntitledTradingEntityDetailsSelector: Selector<GlobalState, TradingEntityDetail[]> = createSelector(
  allActiveTradingEntityDetailsSelector,
  createAuthorisedUserSelector(),
  (activeTradingEntityDetails, authorisedUser) => {
    return activeTradingEntityDetails.filter(tradingEntityDetail => {
      return authorisedUser.tradingEntityDetails.some(d => d.tradingEntityId === tradingEntityDetail.tradingEntity.id)
    })
  }
)
